import { create } from "zustand";
import { supabase } from "../supabase/client";
import type { User } from "@/types";
import type { Session } from "@supabase/supabase-js";
import { debugAuthState } from "./debug";

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: 'google') => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  completeProfile: (updates: Partial<User>) => Promise<User>;
  clearError: () => void;
  ensureSession: () => Promise<Session | null>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: true,
  error: null,

  setUser: (user) => set({ user, loading: false }),
  setSession: (session) => set({ session }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),

  signIn: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null });
      console.log('Attempting sign in:', { email, password });

      const { data: { user }, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      console.log('Sign in response:', { user, signInError });

      if (signInError) {
        console.error('Sign in error:', signInError);
        set({ loading: false, error: signInError.message });
        throw signInError;
      }
      if (!user) {
        console.error('No user returned from sign in');
        set({ loading: false, error: 'No user returned from sign in' });
        throw new Error('No user returned from sign in');
      }

      // Wait for session to be established
      console.log('Waiting for session to be established...');
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Verify session is properly set
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Session verification failed:', sessionError);
        set({ loading: false, error: 'Session could not be established' });
        throw new Error('Session could not be established');
      }
      console.log('Session verified successfully for user:', session.user.id);

      // Additional verification - ensure getUser() also works
      const { data: { user: verifyUser }, error: userError } = await supabase.auth.getUser();
      if (userError || !verifyUser) {
        console.error('User verification failed after session setup:', userError);
        
        // Force session refresh by calling refreshSession
        console.log('Attempting to force session refresh...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshData.session || !refreshData.user) {
          console.error('Session refresh failed:', refreshError);
          
          // Last resort: set session manually using the session we verified
          console.log('Attempting to set session manually...');
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token
          });
          
          if (setSessionError) {
            console.error('Manual session set failed:', setSessionError);
            set({ loading: false, error: 'Session could not be established' });
            throw new Error('Session could not be established');
          }
          
          // Verify the manual session worked
          const { data: { user: finalUser }, error: finalError } = await supabase.auth.getUser();
          if (finalError || !finalUser) {
            console.error('Final session verification failed:', finalError);
            set({ loading: false, error: 'Session verification failed' });
            throw new Error('Session verification failed');
          }
          console.log('Manual session set successful for user:', finalUser.id);
        } else {
          console.log('Session refreshed successfully for user:', refreshData.user.id);
        }
      } else {
        console.log('User verification successful:', verifyUser.id);
      }

      // Fetch complete user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select(`
          *,
          creator_profile:creator_profiles(*)
        `)
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Profile fetch error:', profileError);
        set({ loading: false, error: profileError.message });
        throw profileError;
      }
      if (!profile) {
        console.error('Failed to fetch user profile');
        set({ loading: false, error: 'Failed to fetch user profile' });
        throw new Error('Failed to fetch user profile');
      }

      const mappedUser = mapSupabaseUserToAppUser(profile);
      set({ user: mappedUser, session, loading: false, error: null });
      console.log('Sign in successful, user and session set:', { user: mappedUser, sessionUserId: session?.user?.id });
    } catch (error) {
      console.error('Sign in error (outer catch):', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign in'
      });
      throw error;
    }
  },

  signInWithProvider: async (provider: 'google') => {
    try {
      set({ loading: true, error: null });

      const { data, error: signInError } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signInError) {
        console.error('OAuth sign in error:', signInError);
        set({ loading: false, error: signInError.message });
        throw signInError;
      }

      // OAuth will redirect, so we don't need to handle the success case here
      set({ loading: false });
    } catch (error) {
      console.error('OAuth sign in error (outer catch):', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign in with OAuth'
      });
      throw error;
    }
  },

  signUp: async (email: string, password: string, fullName: string) => {
    try {
      set({ loading: true, error: null });

      const { data: { user }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!user) throw new Error('No user returned from sign up');

      // Create user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .insert({
          id: user.id,
          email: user.email,
          full_name: fullName,
          role: 'user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('Failed to create user profile');

      set({ user: mapSupabaseUserToAppUser(profile), loading: false, error: null });
    } catch (error) {
      console.error('Sign up error:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign up'
      });
      throw error;
    }
  },

  signOut: async () => {
    try {
      set({ loading: true, error: null });

      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;

      set({ user: null, session: null, loading: false, error: null });
    } catch (error) {
      console.error('Sign out error:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to sign out'
      });
      throw error;
    }
  },

  completeProfile: async (updates: Partial<User>) => {
    try {
      const user = get().user;
      if (!user) throw new Error("Not authenticated");

      set({ loading: true, error: null });

      // Update user profile
      const { data: updatedUser, error: updateError } = await supabase
        .from("users")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select(
          `
          *,
          creator_profile:creator_profiles(*)
        `,
        )
        .single();

      if (updateError) throw updateError;
      if (!updatedUser) throw new Error("Failed to update profile");

      set({ user: mapSupabaseUserToAppUser(updatedUser), loading: false, error: null });
      return updatedUser;
    } catch (error) {
      console.error("Profile update error:", error);
      set({
        loading: false,
        error:
          error instanceof Error ? error.message : "Failed to update profile",
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  ensureSession: async () => {
    try {
      const currentState = get();
      if (currentState.session) return currentState.session;

      set({ loading: true, error: null });

      const { data: { session: supabaseSession }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !supabaseSession) {
        console.error('Session verification failed:', sessionError);
        set({ loading: false, error: 'Session could not be established' });
        throw new Error('Session could not be established');
      }
      console.log('Session verified successfully for user:', supabaseSession.user.id);

      set({ session: supabaseSession, loading: false, error: null });
      return supabaseSession;
    } catch (error) {
      console.error('Session verification error:', error);
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to verify session'
      });
      throw error;
    }
  }
}));

function mapSupabaseUserToAppUser(supabaseUser: any): User {
  return {
    ...supabaseUser,
    status: supabaseUser.status || 'active', // default to 'active' if missing
  };
}